import React from "react";
import { sortGroupsAlphabetically, sortSubgroups } from "../helpers";
import { fetchCompanyDetail, fetchNogaDetail } from "./dataFetching";
import { DataQuality } from "@sia/style-guide";

function extractActiveFilters(overheadFilters, filterId, isInitialLoad) {
  const DEFAULT_ITEMS = [];
  let filter = overheadFilters.filters.find((e) => e.id === filterId);
  if (isInitialLoad && filter.id !== "reference") {
    return DEFAULT_ITEMS;
  }
  if (filter) {
    if (filter.id === "reference") {
      let item = filter.value;
      let itemName = item.label;
      return itemName;
    }
    let items = filter.items.filter((e) => e.active).map((e) => e.id);

    return items;
  }
  return DEFAULT_ITEMS;
}

export function extractRequestBodyFromFilters(overheadFilters, isInitialLoad) {
  let years = extractActiveFilters(overheadFilters, "surveyYears", isInitialLoad);
  const nogaCodes = extractActiveFilters(overheadFilters, "nogaCode", isInitialLoad);
  const officeSizeIds = extractActiveFilters(overheadFilters, "officeSize", isInitialLoad);
  const greaterRegions = extractActiveFilters(overheadFilters, "greaterRegion", isInitialLoad);
  const accounts = extractActiveFilters(overheadFilters, "accounts", isInitialLoad);
  const reference = extractActiveFilters(overheadFilters, "reference", isInitialLoad);

  let body = {
    years,
    nogaCodes,
    officeSizeIds,
    greaterRegions,
    accounts,
    reference,
  };

  return body;
}

async function setInitialActiveNoga(filtersCopy, languageKey, nogaFilterIndex, companyAccess) {
  let onError = (err) => console.log(err);
  let companyDetail = await fetchCompanyDetail(onError, companyAccess);
  let { nogaId } = companyDetail.data[0];
  let nogaDetails = await fetchNogaDetail(languageKey, nogaId, onError);
  let { nogaCode } = nogaDetails.data;
  if (nogaFilterIndex !== -1) {
    filtersCopy.filters[nogaFilterIndex].items = filtersCopy.filters[nogaFilterIndex].items.map((option) => ({
      ...option,
      active: option.id === nogaCode,
    }));
    const noActiveNogas = filtersCopy.filters[nogaFilterIndex].items.every((item) => !item.active);
    // If the company doesn't have noga
    // Activate The Archi office as default selected noga
    if (noActiveNogas) {
      activateDefaultNogaCode(filtersCopy, nogaFilterIndex);
    }
  }
  return filtersCopy;
}

function setInitialActiveSurveyYear(filtersCopy) {
  let surveyYearsFilterIndex = filtersCopy.filters.findIndex((e) => e.id === "surveyYears");
  let currentYear = new Date().getFullYear();
  filtersCopy.filters[surveyYearsFilterIndex].items = filtersCopy.filters[surveyYearsFilterIndex].items
    .filter((e) => e.id === currentYear)
    .map((e) => ({ ...e, active: true }));
  return filtersCopy;
}

function setInitialActiveAccounts(filtersCopy) {
  let accountsFilterIndex = filtersCopy.filters.findIndex((e) => e.id === "accounts");
  filtersCopy.filters[accountsFilterIndex].items = filtersCopy.filters[accountsFilterIndex].items.map((e) => ({ ...e, active: true }));
  return filtersCopy;
}

function activateDefaultNogaCode(filtersCopy, nogaFilterIndex) {
  const ARCHI_OFFICE_NOGA_CODE = 711101;
  filtersCopy.filters[nogaFilterIndex].items = filtersCopy.filters[nogaFilterIndex].items.map((option) => ({
    ...option,
    active: option.id === ARCHI_OFFICE_NOGA_CODE,
  }));
  return filtersCopy;
}

function updateFilterItems(filtersCopy, filter, submitedFilters, nextAvailableFilters) {
  let updatedFilterItems =
    filtersCopy.allAvailabeFilters[filter.id]?.map((item) => {
      let dropDownOption = {};
      if (filter.id === "surveyYears") {
        dropDownOption = { id: item, label: item, active: submitedFilters[filter.id].includes(item) };
        return dropDownOption;
      }
      let filterIds = nextAvailableFilters[filter.id].map((e) => e.id);
      dropDownOption = {
        id: item.id,
        label: item.value,
        name: item.value,
        active: submitedFilters[filter.id].includes(item.id),
        disabled: !filterIds.includes(item.id),
      };
      return dropDownOption;
    }) || [];
  return updatedFilterItems;
}

export async function updateFilters(filters, submitedFilters, nextAvailableFilters, isInitialLoad, isMasterKeyValid, languageKey) {
  let filtersCopy = { ...filters };

  if (isInitialLoad) {
    filtersCopy.allAvailabeFilters = nextAvailableFilters;
  }

  filtersCopy.filters = filtersCopy.filters.map((filter) => {
    let filterCopy = { ...filter };
    if (filter.id !== "reference") {
      filterCopy.items = updateFilterItems(filtersCopy, filter, submitedFilters, nextAvailableFilters);
    }
    return filterCopy;
  });
  let nogaFilterIndex = filtersCopy.filters.findIndex((e) => e.id === "nogaCode");
  if (isInitialLoad && isMasterKeyValid) {
    filtersCopy = await setInitialActiveNoga(filtersCopy, languageKey, nogaFilterIndex, isMasterKeyValid);
    filtersCopy = setInitialActiveSurveyYear(filtersCopy);
    filtersCopy = setInitialActiveAccounts(filtersCopy);
    return { ...filtersCopy, newest: true, isInitialLoad: false };
  } else if (isInitialLoad && !isMasterKeyValid) {
    filtersCopy = activateDefaultNogaCode(filtersCopy, nogaFilterIndex);
    filtersCopy = setInitialActiveSurveyYear(filtersCopy);
    filtersCopy = setInitialActiveAccounts(filtersCopy);
    return { ...filtersCopy, newest: true, isInitialLoad: false };
  }

  return { ...filtersCopy, newest: false, isInitialLoad: false };
}

export function companyHasOwnValues(data) {
  let allocations = data.overheadsAllocationPerPeriod[0];
  let accounts = allocations.accounts;
  let overheadAllocation = accounts[0].metrics.overheadAllocation;
  const propertyName = "companyValues";
  let result = overheadAllocation.hasOwnProperty(propertyName);
  return result;
}

function initializeParentAccount(acc, account, hasOwnValues) {
  let meanAdjustedTurnoverRef = account.metrics.overheadAllocation.meanAdjustedTurnover;
  let meanGrossPayrollRef = account.metrics.overheadAllocation.meanGrossPayroll;
  acc.push({ group: account.accountName, subgroup: "average", referenceAverages: [meanAdjustedTurnoverRef, meanGrossPayrollRef] });
  if (hasOwnValues) {
    let ownAdjustedTurnover = account.metrics.overheadAllocation.companyValues.adjustedTurnover;
    let ownGrossPayroll = account.metrics.overheadAllocation.companyValues.grossPayroll;
    acc.push({ group: account.accountName, subgroup: "ownValue", referenceAverages: [ownAdjustedTurnover, ownGrossPayroll] });
  }
}

function updateParentAccount(acc, account, hasOwnValues, array) {
  let parentAccount = array.find((e) => e.accountId === account.parentAccountId);
  let averageIndex = acc.findIndex((e) => e.group === parentAccount.accountName && e.subgroup === "average");
  acc[averageIndex][account.accountName] = [
    account.metrics.overheadAllocation.meanAdjustedTurnover,
    account.metrics.overheadAllocation.meanGrossPayroll,
  ];
  if (hasOwnValues) {
    let ownValueIndex = acc.findIndex((e) => e.group === parentAccount.accountName && e.subgroup === "ownValue");
    let ownAdjustedTurnover = account.metrics.overheadAllocation.companyValues.adjustedTurnover;
    let ownGrossPayroll = account.metrics.overheadAllocation.companyValues.grossPayroll;
    acc[ownValueIndex][account.accountName] = [ownAdjustedTurnover, ownGrossPayroll];
  }
}

// Stackedbarchart data
export function parseStackedbarChartData(data, hasOwnValues) {
  let allocations = data.overheadsAllocationPerPeriod[0];
  let accounts = allocations.accounts;
  let accountsToExcludeIds = [61, 62];
  let stackedBarchartData = accounts.reduce((acc, account, idx, array) => {
    if (!accountsToExcludeIds.includes(account.accountId)) {
      if (account.parentAccountId === null) {
        initializeParentAccount(acc, account, hasOwnValues);
      } else {
        updateParentAccount(acc, account, hasOwnValues, array);
      }
    }
    return acc;
  }, []);

  stackedBarchartData.sort(sortGroupsAlphabetically);
  return stackedBarchartData;
}
// End Stackedbarchart data

const translateToPercentage = (value) => Math.abs(Number.parseFloat((value * 100).toFixed(1)));
const formatPercentage = (value) => `${value} %`;
const formatTotalPercentage = (value) => (value === null || value === undefined || value === 0 ? "-" : `${value} %`);

function buildFooterRow({ label, officeSizeValues, averageValue, hasOwnValues, myOfficeValue, myOfficeDeltaValue, valueFormatter, rowClassName }) {
  const firstCell = {
    value: label,
    className: `left-align ${rowClassName || ""}`.trim(),
  };
  const qualityCell = {
    value: null,
    className: "average average-cell",
  };

  const officeCells = officeSizeValues.map((v) => ({
    value: valueFormatter(v),
    className: "average",
  }));
  const averageCell = {
    value: valueFormatter(averageValue),
    className: "average average-cell",
  };

  const ownValueCells = hasOwnValues
    ? [
        { value: valueFormatter(myOfficeValue), className: "own-value" },
        { value: valueFormatter(myOfficeDeltaValue), className: "own-value" },
      ]
    : [];

  return {
    mainRow: [firstCell, qualityCell, ...officeCells, averageCell, ...ownValueCells],
    subRows: [],
    hasOwnValues: false,
    total: true,
    rowClassName,
  };
}

function computeMyOfficeTotalFromAccounts(accounts, refIndex) {
  const accountsToExcludeIds = [61, 62];
  const dynamicProperty = refIndex === 0 ? "adjustedTurnover" : "grossPayroll";
  return accounts
    .filter((a) => a.parentAccountId === null && !accountsToExcludeIds.includes(a.accountId))
    .reduce((sum, a) => {
      const v = a?.metrics?.overheadAllocation?.companyValues?.[dynamicProperty];
      return sum + (typeof v === "number" ? v : 0);
    }, 0);
}

// Generate data row
function generateDataRow(account, refIndex, hasOwnValues, officeSizeItems, translate, showQuality) {
  let dynamicProperty = refIndex === 0 ? "meanAdjustedTurnover" : "meanGrossPayroll";
  let mainRow = [];
  const quality = showQuality ? account?.metrics?.overheadAllocation?.quality : null;

  let firstCell = {
    value: account.accountName,
    className: "left-align",
  };
  let qualityCell = {
    value: quality ? (
      <div className="data-quality-cell__content">
        <DataQuality translate={translate} numberOfRecords={quality.count} coefficient={quality.coefficientOfVariation} compact={true} />
      </div>
    ) : (
      <div className="data-quality-cell__content" />
    ),
    className: "data-quality-cell",
  };

  let tables = account.metrics.table;
  let officeSizes = tables.filter((table) => table.officeSizeId !== null);
  let officeSizeCells = officeSizeItems.map((item) => {
    let index = officeSizes.findIndex((e) => e.officeSizeId === item.id);
    if (index >= 0) {
      return {
        value: formatPercentage(translateToPercentage(officeSizes[index].metrics[dynamicProperty])),
        className: "average",
      };
    } else {
      return {
        value: "-",
        className: "average",
      };
    }
  });
  let parentTable = tables.find((table) => table.officeSizeId === null);
  let average = parentTable.metrics[dynamicProperty];
  let averageCell = { value: formatPercentage(translateToPercentage(average)), className: "average average-cell" };

  let ownValueCells = [];
  if (hasOwnValues) {
    dynamicProperty = refIndex === 0 ? "adjustedTurnover" : "grossPayroll";
    ownValueCells = [
      { value: formatPercentage(translateToPercentage(account.metrics.overheadAllocation.companyValues[dynamicProperty])), className: "own-value" },
      {
        value: formatPercentage(translateToPercentage(account.metrics.overheadAllocation.companyValues[dynamicProperty] - average)),
        className: "own-value",
      },
    ];
  }
  mainRow.push(firstCell, qualityCell, ...officeSizeCells, averageCell, ...ownValueCells);
  return mainRow;
}

// End of Generate data row
function buildTotalOverheadsFooterRow(data, refIndex, hasOwnValues, officeSizeItems, translate) {
  const period = data.overheadsAllocationPerPeriod?.[0];
  const totals = period?.totalOverheadsToTurnover ?? [];

  const officeSizeValues = officeSizeItems.map((item) => {
    const t = totals.find((x) => x.officeSizeId === item.id);
    return t?.totalOverheadTurnoverAdjusted ?? null;
  });

  const avgEntry = totals.find((x) => x.officeSizeId === null) ?? null;
  const averageValue =
    avgEntry?.totalOverheadTurnoverAdjusted ??
    (() => {
      const nums = officeSizeValues.filter((v) => typeof v === "number");
      if (!nums.length) return null;
      return nums.reduce((a, b) => a + b, 0) / nums.length;
    })();

  const myOfficeValue = hasOwnValues ? computeMyOfficeTotalFromAccounts(period?.accounts ?? [], refIndex) : null;
  const myOfficeDeltaValue =
    hasOwnValues && typeof myOfficeValue === "number" && typeof averageValue === "number" ? myOfficeValue - averageValue : null;

  return buildFooterRow({
    label: translate("benchmarking.evaluations.overheads.turnover-percentage"),
    officeSizeValues,
    averageValue,
    hasOwnValues,
    myOfficeValue,
    myOfficeDeltaValue,
    valueFormatter: (v) => formatTotalPercentage(translateToPercentage(v)),
    rowClassName: "footer-row",
  });
}

// Datatable data rows
export function parseOverheadsTableData(data, refIndex, hasOwnValues, officeSizeItems, translate) {
  const accounts = data.overheadsAllocationPerPeriod[0].accounts;
  const accountsToExcludeIds = [61, 62];
  const sortedAccounts = [...accounts].sort((a, b) => (a.accountName || "").localeCompare(b.accountName || ""));
  let rows =
    sortedAccounts.reduce((acc, account, idx, array) => {
      const parentAccountId = account.parentAccountId;
      const accountId = account.accountId;

      if (parentAccountId === null && !accountsToExcludeIds.includes(accountId)) {
        let mainRow = generateDataRow(account, refIndex, hasOwnValues, officeSizeItems, translate, true);
        let subRows = sortedAccounts
          .filter((child) => child.parentAccountId === accountId)
          .sort((a, b) => (a.accountName || "").localeCompare(b.accountName || ""))
          .map((child) => generateDataRow(child, refIndex, hasOwnValues, officeSizeItems, translate, false));
        acc.push({ mainRow, subRows, hasOwnValues });
      }
      return acc;
    }, []) || [];
  rows.push(buildTotalOverheadsFooterRow(data, refIndex, hasOwnValues, officeSizeItems, translate));
  return rows;
}
// End Datatable data rows
